import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import agentService from "../../services/agentService";
import { Box, Button, Alert } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import PageLoader from "../shared/PageLoader";
import AgentTestPlayground from "../shared/AgentTestPlayground";
import styles from "./AgentPlayground.module.scss";

const AgentPlayground = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get("id");
  const isTemplate = searchParams.get("template") === "true";
  // Set by Shared with me for agents a partner shared across companies.
  const sharedVia = searchParams.get("via");

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: "#e36250 !important",
      },
      secondary: {
        main: "#8b5cf6",
      },
    },
  });

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadAgent = async () => {
      if (!agentId) {
        setError("No agent ID provided");
        setLoading(false);
        return;
      }

      try {
        let agentData;
        
        // If coming from templates, use template API
        if (isTemplate) {
          try {
            const templateData = await agentService.getTemplateByAgentId(agentId);
            console.log("Template data loaded:", templateData);
            
            // Get variables from template data (can be array or string)
            const agentVariables = templateData.agentVariables || templateData.agent_variables || [];
            const agentChainedVariables = templateData.agentChainedVariables || templateData.agent_chained_variables || [];
            
            // Convert variables to string format if array (AgentTestPlayground handles both)
            const variablesString = Array.isArray(agentVariables) 
              ? agentVariables.join(',') 
              : (typeof agentVariables === 'string' ? agentVariables : '');
            
            // Map template data to agent format expected by AgentTestPlayground
            agentData = {
              id: templateData.agentId || templateData.agent_id,
              name: templateData.agentName || templateData.agent_name,
              description: templateData.agentDescription || templateData.agent_description,
              variables: variablesString,
              // Store chained variables - AgentTestPlayground will use these if agent_chaining is not available
              agentChainedVariables: agentChainedVariables,
              // Include template metadata for reference
              templateId: templateData.id,
              userId: templateData.userId || templateData.user_id,
              userName: templateData.userName || templateData.user_name,
            };
          } catch (templateErr) {
            // If template not found, show specific error
            if (templateErr.response?.status === 404 || templateErr.status === 404) {
              const errorDetail = templateErr.response?.data?.detail || templateErr.data?.detail || templateErr.message;
              setError(errorDetail || `Template for agent ID ${agentId} not found`);
              setLoading(false);
              return;
            }
            // For other errors, show error message
            console.error("Error loading template:", templateErr);
            const errorDetail = templateErr.response?.data?.detail || templateErr.data?.detail || templateErr.message;
            setError(errorDetail || "Failed to load template");
            setLoading(false);
            return;
          }
        } else {
          // Regular agent loading
          agentData = await agentService.getAgent(agentId);
        }
        
        setAgent(agentData);
      } catch (err) {
        console.error("Error loading agent:", err);
        if (sharedVia && err.status === 404) {
          // The share was revoked, or the company left the partner (Partner tier
          // guide §10): re-check what this session may still see.
          window.dispatchEvent(new Event("rbac:refresh"));
          setError("This agent is no longer shared with you.");
          return;
        }
        const errorMessage = err.response?.data?.detail || err.data?.detail || err.message || "Failed to load agent";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadAgent();
  }, [agentId, isTemplate, sharedVia]);

  if (loading) {
    return (
      <ThemeProvider theme={muiTheme}>
        <PageLoader message="Loading agent playground..." />
      </ThemeProvider>
    );
  }

  if (error || !agent) {
    return (
      <ThemeProvider theme={muiTheme}>
        <Box className={styles.errorContainer}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || "Agent not found"}
          </Alert>
          <Button variant="contained" startIcon={<ArrowBack />} onClick={() => navigate(isTemplate ? "/templates" : "/agents")}>
            {isTemplate ? "Back to Templates" : "Back to Agents"}
          </Button>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Box className={styles.playgroundContainer}>
        <AgentTestPlayground
          variant="page"
          agentId={agentId}
          agent={agent}
          variables={agent?.variables}
          sharedVia={sharedVia}
          // A template id is not a shared agent — nothing to look up for it.
          enableMyDim3={!isTemplate}
          onBackClick={() => navigate(isTemplate ? "/templates" : "/agents")}
          containerSx={{ flex: 1, minHeight: 0 }}
        />
      </Box>
    </ThemeProvider>
  );
};

export default AgentPlayground;
